import { postApiNoHeader } from 'redux/apis';
import type { DetectedStructure } from './types';

// PDF upload for the manual Smart Import wizard. Unlike loadFile() (CSV/XLSX,
// parsed entirely client-side), a PDF is sent to the backend's
// POST /employee-cost/import/pdf/extract, which rasterizes/extracts it via
// Claude and returns the SAME DetectedStructure shape loadFile() produces —
// see PDF_SMART_IMPORT_TODO.md. This is what lets Steps 2-4 work completely
// unchanged regardless of whether the source file was a PDF, CSV, or XLSX.

export interface PdfAdjustmentRow {
	cells: Record<string, string>;
	rowType: 'line_item' | 'subtotal';
	rawEmployeeNameText: string | null;
	confidence: 'high' | 'low';
}

export interface PdfAdjustmentTable {
	pageNumbers: number[];
	tableTitle: string;
	tableRole: string;
	columns: Array<{ label: string; dataType: string }>;
	rows: PdfAdjustmentRow[];
}

// benefitType mirrors the backend's BenefitType enum (claudePdfExtractor.ts) —
// keep in sync by hand if that changes.
export type PdfBenefitType =
	| 'health'
	| 'dental'
	| 'vision'
	| 'life'
	| 'disability_std'
	| 'disability_ltd'
	| 'critical_illness'
	| 'accident'
	| 'other'
	| null;

export interface PdfExtractionMetadata {
	documentType: string;
	carrierName: string | null;
	benefitType: PdfBenefitType;
	billingPeriod: { start: string | null; end: string | null } | null;
	statedGrandTotal: number | null;
	// Sum of the totals the employee table itself prints (per-employee totals /
	// sub-totals) — excludes previous balances and payments that the invoice's
	// "total due" (statedGrandTotal) includes. See mergeToDetectedStructure.ts.
	printedDetailTotal: number | null;
	excludedPages: number[];
	adjustmentContext: PdfAdjustmentTable[];
	warnings: string[];
	// P2.2: per-row AI extraction confidence, same order/length as the data rows
	// in rawRows (rawRows.slice(1)) — index i here is rawRows[i + 1].
	rowConfidence: Array<'high' | 'low'>;
}

// Wraps one PdfExtractionMetadata per uploaded document — always the shape
// returned now, even for a single-PDF upload (the N=1 case of the same
// backend code path, joinAcrossDocuments.ts). `statedGrandTotal` and
// `rowConfidence` here are already combined across documents by the backend
// (summed / concatenated respectively) so Step 3/Step 4's existing reads of
// state.pdfExtraction.statedGrandTotal / .rowConfidence work unchanged.
export interface PdfExtractionSummary {
	documents: PdfExtractionMetadata[];
	statedGrandTotal: number | null;
	printedDetailTotal: number | null;
	rowConfidence: Array<'high' | 'low'>;
}

export interface PdfDetectedStructure extends DetectedStructure {
	extraction: PdfExtractionSummary;
}

const PDF_EXTRACT_URL = '/employee-cost/import/pdf/extract';

/**
 * Uploads one or more carrier-invoice PDFs to the backend extraction
 * endpoint in a single request and returns ONE combined DetectedStructure
 * (plus `extraction` metadata) matching what loadFile() produces for
 * CSV/XLSX — same shape, so the rest of the wizard doesn't need to know
 * which path a given file came through, or how many PDFs were uploaded.
 *
 * `signal` allows the caller to cancel a long-running extraction (this can
 * take 30-90+ seconds per document for a multi-page scanned invoice — see
 * Step1Upload's progress UI).
 */
export async function loadPdfsViaServer(
	files: File[],
	companyId: string,
	payPeriodId: string,
	signal?: AbortSignal
): Promise<PdfDetectedStructure> {
	const formData = new FormData();
	for (const file of files) {
		formData.append('file', file);
	}
	formData.append('companyId', companyId);
	formData.append('payPeriodId', payPeriodId);

	const response = await postApiNoHeader(PDF_EXTRACT_URL, formData, signal);
	// A long extraction is kept alive past the AWS load balancer's idle
	// timeout by the backend committing to a 200 early (see
	// pdfImportController.ts), so a failure after that point arrives as a 200
	// with `streamedError` rather than as an HTTP error status.
	const failure = response.data as { streamedError?: boolean; message?: string };
	if (failure?.streamedError) {
		throw new Error(failure.message || 'Failed to process PDF');
	}
	const data = response.data as PdfDetectedStructure;

	return {
		...data,
		splitNameDetected: data.nameColumnConfig?.type === 'combined',
	};
}
